import type { UserPreferences, UserPreferencesPatch } from "./types";

export const ACTIVE_PREFERENCES_USER_KEY = "rivetai-preferences-active-user";
const USER_PREFERENCES_KEY_PREFIX = "rivetai-user-preferences";
const PENDING_PREFERENCES_KEY_PREFIX = "rivetai-user-preferences-pending";

function storageKey(prefix: string, userId: string): string {
  return `${prefix}:${userId}`;
}

function readJson<T>(key: string): T | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

export function mergeUserPreferences(
  current: UserPreferences,
  patch: UserPreferencesPatch
): UserPreferences {
  return {
    ...current,
    ...(patch.appearance && {
      appearance: { ...current.appearance, ...patch.appearance } as UserPreferences["appearance"],
    }),
    ...(patch.scriptEditor && {
      scriptEditor: { ...current.scriptEditor, ...patch.scriptEditor },
    }),
    ...(patch.budget && {
      budget: { ...current.budget, ...patch.budget },
    }),
    ...(patch.onboarding && {
      onboarding: { ...current.onboarding, ...patch.onboarding },
    }),
    ...(patch.navigation && {
      navigation: { ...current.navigation, ...patch.navigation },
    }),
  };
}

export function readCachedUserPreferences(userId: string): UserPreferences {
  return readJson<UserPreferences>(storageKey(USER_PREFERENCES_KEY_PREFIX, userId)) ?? {};
}

export function writeCachedUserPreferences(userId: string, preferences: UserPreferences): void {
  writeJson(storageKey(USER_PREFERENCES_KEY_PREFIX, userId), preferences);
}

export function queueUserPreferencesPatch(
  userId: string,
  patch: UserPreferencesPatch
): UserPreferences {
  const next = mergeUserPreferences(readCachedUserPreferences(userId), patch);
  writeCachedUserPreferences(userId, next);
  writePendingUserPreferences(
    userId,
    mergeUserPreferences(readPendingUserPreferences(userId), patch)
  );
  return next;
}

export function readPendingUserPreferences(userId: string): UserPreferencesPatch {
  return readJson<UserPreferencesPatch>(storageKey(PENDING_PREFERENCES_KEY_PREFIX, userId)) ?? {};
}

export function writePendingUserPreferences(
  userId: string,
  preferences: UserPreferencesPatch
): void {
  writeJson(storageKey(PENDING_PREFERENCES_KEY_PREFIX, userId), preferences);
}

export function clearPendingUserPreferences(userId: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(storageKey(PENDING_PREFERENCES_KEY_PREFIX, userId));
}

export function setActivePreferencesUser(userId: string | null): void {
  if (typeof window === "undefined") return;

  if (userId) {
    window.localStorage.setItem(ACTIVE_PREFERENCES_USER_KEY, userId);
  } else {
    window.localStorage.removeItem(ACTIVE_PREFERENCES_USER_KEY);
  }
}

export function clearImportedLegacyPreferences(userId: string, preferences: UserPreferences): void {
  if (typeof window === "undefined") return;

  if (preferences.appearance) {
    window.localStorage.removeItem("rivetai-appearance");
  }
  if (preferences.scriptEditor?.pageTheme) {
    window.localStorage.removeItem("rivetai-script-editor-page-theme");
  }
  if (preferences.scriptEditor?.recentTagCategoryNames) {
    window.localStorage.removeItem("rivetai.script-editor.recent-tag-categories");
  }
  if (preferences.budget?.topSheetViewMode) {
    window.localStorage.removeItem(`rivetai:budget-topsheet-view-mode:${userId}`);
  }
  if (preferences.onboarding) {
    window.localStorage.removeItem(`onboarding_dismissed_${userId}`);
    window.localStorage.removeItem(`onboarding_in_progress_${userId}`);
    window.localStorage.removeItem(`onboarding_step_${userId}`);
  }
}
